import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircleHeart, Search, CalendarCheck, KeyRound, ShieldCheck, Clock, Users, MapPin, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import ListingCard from "../components/ListingCard";

const HERO_IMG = "https://images.unsplash.com/photo-1645402302245-549fd0d5a272?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.4 } };

export default function Home() {
  const [content, setContent] = useState({});
  const [hoods, setHoods] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [showCta, setShowCta] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowCta(window.scrollY > 420);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/neighborhoods").then((r) => setHoods(r.data.filter((h) => h.listings_count > 0).slice(0, 6))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data.slice(0, 4))).catch(() => {});
    api.get("/listings").then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <SEO title="Tendly — Find your exact home | AI Rental Concierge, Vancouver"
        description="Tendly is a free AI rental concierge for Vancouver, BC. Answer a few questions, get matched with real long-term rentals, and a human agent books your viewings."
        jsonLd={{
          "@context": "https://schema.org", "@type": "Organization", name: "Tendly",
          description: "AI-powered rental concierge for long-term rentals in Vancouver, BC.",
          address: { "@type": "PostalAddress", addressLocality: "Kitsilano", addressRegion: "BC", addressCountry: "CA" },
          email: "tendly.concierge@gmail.com", telephone: "+1-778-956-6372",
        }} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Vancouver skyline at dusk" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-teal-900/80 via-teal-900/60 to-background" />
        </div>
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pt-24 pb-32 md:pt-36 md:pb-44">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-teal-100 text-xs uppercase tracking-widest font-semibold">Free for renters, always · Vancouver, BC</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white max-w-3xl">
            {content.hero_title || "Tell us what home feels like. We'll find it."}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-6 text-base md:text-lg text-teal-50/90 max-w-xl leading-relaxed">
            {content.hero_subtitle || "Answer a few questions in our live chat, get matched with real rentals instantly, and a human agent books your viewings."}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3">
            <Link to="/chat" data-testid="hero-open-chat-button"
              className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-3.5 font-semibold text-base hover:bg-coral-600 transition-colors shadow-lg shadow-coral-500/30">
              <MessageCircleHeart className="w-5 h-5" /> Open live chat to find your perfect home
            </Link>
            <Link to="/listings" data-testid="hero-browse-button"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/25 text-white px-7 py-3.5 font-semibold hover:bg-white/20 transition-colors">
              Browse listings
            </Link>
          </motion.div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-teal-50/80 text-sm">
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> Agent reply within 24–48h</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Your data stays private (PIPEDA)</span>
            <span className="inline-flex items-center gap-2"><Users className="w-4 h-4" /> Real human agents</span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 md:py-24">
        <motion.div {...fade}>
          <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">How it works</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Three steps to your front door</h2>
        </motion.div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            { icon: <MessageCircleHeart className="w-6 h-6" />, t: "Tell Tendly", d: "Open the chat and describe your exact home — neighbourhood, budget, beds, pets, move-in date. Takes about four minutes." },
            { icon: <Search className="w-6 h-6" />, t: "Get matched", d: "We instantly match you against live listings reviewed by our team — perfect matches first, then similar homes ranked by match score." },
            { icon: <KeyRound className="w-6 h-6" />, t: "Tour & sign", d: "Pick your favourites, share your weekly availability, and a human agent confirms your viewings within 24–48 hours." },
          ].map((s, i) => (
            <motion.div key={s.t} {...fade} transition={{ delay: i * 0.1 }} className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm">
              <span className="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-900/40 text-primary flex items-center justify-center">{s.icon}</span>
              <h3 className="mt-5 text-xl font-semibold tracking-tight">{i + 1}. {s.t}</h3>
              <p className="mt-2 text-muted-foreground text-sm leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured listings */}
      {featured.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-16 md:pb-24">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Fresh inventory</p>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Featured homes this week</h2>
            </div>
            <Link to="/listings" data-testid="view-all-listings" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
          </div>
        </section>
      )}

      {/* Neighborhoods */}
      <section className="bg-secondary/60 border-y border-border">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 md:py-24">
          <motion.div {...fade}>
            <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Neighbourhoods</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Where in Vancouver do you belong?</h2>
          </motion.div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {hoods.map((h, i) => (
              <motion.div key={h.slug} {...fade} transition={{ delay: i * 0.05 }}>
                <Link to={`/rentals/${h.slug}`} data-testid={`hood-${h.slug}`}
                  className="block rounded-2xl border border-border bg-card p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg tracking-tight flex items-center gap-2"><MapPin className="w-4 h-4 text-accent" /> {h.name}</h3>
                    {h.listings_count > 0 && <span className="text-xs font-semibold text-primary">{h.listings_count} homes</span>}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{h.blurb}</p>
                  {h.avg_1br_rent && <p className="mt-3 text-xs text-muted-foreground">Avg 1-bed from <span className="font-semibold text-foreground">${h.avg_1br_rent.toLocaleString()}/mo</span></p>}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust + sources */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade}>
            <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Why Tendly</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">A concierge, not a classifieds site</h2>
            <ul className="mt-8 space-y-5">
              {[
                ["Real homes, human-reviewed", "Every listing is submitted or reviewed by our team before it goes live — no ghost ads, no duplicates."],
                ["Matched, not just filtered", "Our matching weighs location, budget, beds, pets, accessibility and more — and shows you the match score."],
                ["Humans close the loop", "AI does the searching; real agents confirm your viewings with the property and coordinate everything."],
                ["Free, forever", "Renters never pay a cent — no fees, no marked-up rent, ever."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-4">
                  <span className="mt-1 w-6 h-6 rounded-full bg-teal-50 dark:bg-teal-900/40 text-primary flex items-center justify-center shrink-0"><CalendarCheck className="w-3.5 h-3.5" /></span>
                  <div><p className="font-semibold">{t}</p><p className="text-sm text-muted-foreground mt-1 leading-relaxed">{d}</p></div>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fade} className="rounded-2xl border border-border bg-card p-8 shadow-sm">
            <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Our promise</p>
            <ul className="mt-6 space-y-5">
              {[
                [ShieldCheck, "Free for renters, always", "You never pay a cent — and your rent is never marked up."],
                [Users, "Real human agents", "A person confirms every viewing with the property before you go."],
                [Clock, "24–48 hour response", "We follow up on every request within one to two days."],
              ].map(([Icon, t, d]) => (
                <li key={t} className="flex gap-4 items-start">
                  <span className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-900/40 text-primary flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
                  <div><p className="font-semibold">{t}</p><p className="text-sm text-muted-foreground mt-0.5">{d}</p></div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Testimonials — only shown once real ones exist */}
      {testimonials.length > 0 && (
      <section className="bg-secondary/60 border-y border-border">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 md:py-24">
          <motion.div {...fade}>
            <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Renters like you</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Found their exact home</h2>
          </motion.div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.figure key={t.id || i} {...fade} transition={{ delay: i * 0.05 }} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <blockquote className="text-sm leading-relaxed text-foreground/90">“{t.text}”</blockquote>
                <figcaption className="mt-4 text-sm font-semibold">{t.name} <span className="font-normal text-muted-foreground">· {t.area}</span></figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Landlord CTA + pricing summary */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-16 md:pb-24">
        <div className="rounded-2xl bg-primary text-primary-foreground p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-teal-100/80">For landlords & property managers</p>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Own a rental property?</h2>
              <p className="mt-4 text-primary-foreground/85 leading-relaxed">Whether you manage one condo or a 200-unit portfolio, Tendly delivers qualified, concierge-matched renter leads for a flat monthly fee.</p>
              <p data-testid="home-trial-line" className="mt-4 font-semibold text-lg">Get a 1 month free trial to see the traction for yourself.</p>
              <a href="/for-landlords" data-testid="home-landlord-cta"
                className="mt-6 inline-block rounded-full bg-accent text-accent-foreground px-7 py-3 font-semibold hover:bg-coral-600 transition-colors">
                List free for your first month and see the traction for yourself
              </a>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["Per Listing", "$50/listing/mo", "1–2 properties"],
                ["Starter", "$250/mo", "up to 10 listings"],
                ["Growth", "$500/mo", "10–20 listings"],
                ["Unlimited", "$1,000/mo", "20+ listings"],
              ].map(([n, pr, d]) => (
                <div key={n} className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-4">
                  <p className="font-semibold text-sm">{n}</p>
                  <p className="mt-1 font-heading font-bold text-xl">{pr}</p>
                  <p className="text-xs text-primary-foreground/70">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ teaser + CTA */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Questions</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Good to know</h2>
            <dl className="mt-8 space-y-6">
              {faqs.map((f, i) => (
                <div key={i}>
                  <dt className="font-semibold">{f.q}</dt>
                  <dd className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
            <Link to="/faq" data-testid="faq-more-link" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">All FAQs <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 flex flex-col justify-center">
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">Ready when you are</h3>
            <p className="mt-4 text-primary-foreground/80 leading-relaxed">Four minutes in chat. Real matches instantly. Viewings booked by a human within 24–48 hours.</p>
            <Link to="/chat" data-testid="cta-open-chat"
              className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold hover:bg-coral-600 transition-colors">
              <MessageCircleHeart className="w-5 h-5" /> Start your search
            </Link>
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA — appears after scrolling past the hero */}
      {showCta && (
        <div data-testid="sticky-mobile-cta" className="fixed bottom-0 inset-x-0 z-40 md:hidden p-3 bg-background/90 backdrop-blur-lg border-t border-border/80 shadow-lg">
          <Link to="/chat" data-testid="sticky-mobile-chat-button"
            className="w-full min-h-[48px] px-5 py-3 rounded-xl bg-accent text-accent-foreground font-semibold text-base shadow-md hover:bg-coral-600 active:scale-95 transition-all flex items-center justify-center gap-2">
            <MessageCircleHeart className="w-5 h-5" /> Find your home — open live chat
          </Link>
        </div>
      )}
    </div>
  );
}
